import {
  IE_INDICATORS,
  UF_LIST,
  formatDocument,
  partyInputSchema,
  partyUpdateSchema,
  toFormMoney,
  type Party,
  type PermissionCode,
} from "@cafegestor/shared";
import { useState, type FormEvent } from "react";
import {
  Badge,
  Button,
  Card,
  Field,
  Input,
  PageTitle,
  Select,
  Spinner,
  Table,
} from "@/components/ui";
import { useCan } from "@/app/me";
import { DecimalInput } from "@/components/DecimalInput";
import {
  issuesToErrors,
  str,
  useCadastroList,
  useCadastroOptions,
  useForm,
  useListState,
  useSave,
} from "./lib";
import { Checkbox, ListToolbar, Pager, SaveError, StatusBadge } from "./widgets";
import { useAutoCode } from "./auto-code";
import { CodeField } from "./CodeField";

type Papel = "cliente" | "fornecedor";
const TEXT: Record<Papel, { title: string; description: string; novo: string }> = {
  cliente: {
    title: "Clientes",
    description:
      "Quem compra. Vendedor, tabela de preço e forma de pagamento aqui viram o padrão das vendas.",
    novo: "Novo cliente",
  },
  fornecedor: {
    title: "Fornecedores",
    description: "De quem a empresa compra (café cru, embalagens, insumos).",
    novo: "Novo fornecedor",
  },
};
const NOUN: Record<Papel, string> = { cliente: "Cliente", fornecedor: "Fornecedor" };
const PERMISSION: Record<Papel, PermissionCode> = {
  cliente: "cadastros.clientes.gerenciar",
  fornecedor: "cadastros.fornecedores.gerenciar",
};

export function PartiesPage({ papel }: { papel: Papel }) {
  const can = useCan();
  const canEdit = can(PERMISSION[papel]);
  const ls = useListState();
  const [editing, setEditing] = useState<Party | "new" | null>(null);
  const list = useCadastroList<Party>("pessoas", { ...ls, extra: { papel } });
  const t = TEXT[papel];

  return (
    <div className="space-y-6">
      <PageTitle
        title={t.title}
        description={t.description}
        actions={
          canEdit && (
            <Button onClick={() => setEditing("new")} disabled={editing === "new"}>
              {t.novo}
            </Button>
          )
        }
      />
      {editing && (
        <PartyEditor
          key={editing === "new" ? "new" : editing.id}
          papel={papel}
          item={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
        />
      )}
      <Card>
        <ListToolbar
          q={ls.q}
          onQ={ls.setQ}
          status={ls.status}
          onStatus={ls.setStatus}
          placeholder="Buscar por código, nome ou CPF/CNPJ"
        />
        {list.isPending ? (
          <Spinner />
        ) : (
          <>
            <Table head={["Código", "Nome", "CPF/CNPJ", "Cidade/UF", "Vendedor", "Situação", ""]}>
              {list.data?.items.map((p) => (
                <tr key={p.id}>
                  <td className="px-3 py-2 font-mono">{p.code}</td>
                  <td className="px-3 py-2">
                    <div className="font-medium">{p.name}</div>
                    {p.tradeName && (
                      <div className="text-xs text-muted-foreground">{p.tradeName}</div>
                    )}
                    {p.isCustomer && p.isSupplier && (
                      <Badge>{papel === "cliente" ? "também fornecedor" : "também cliente"}</Badge>
                    )}
                  </td>
                  <td className="px-3 py-2">{formatDocument(p.document) || "—"}</td>
                  <td className="px-3 py-2">{p.city ? `${p.city}/${p.uf ?? ""}` : "—"}</td>
                  <td className="px-3 py-2">{p.salespersonName ?? "—"}</td>
                  <td className="px-3 py-2">
                    <StatusBadge status={p.status} />
                  </td>
                  <td className="px-3 py-2 text-right">
                    {canEdit && (
                      <Button size="sm" variant="ghost" onClick={() => setEditing(p)}>
                        Editar
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </Table>
            <Pager total={list.data?.total ?? 0} offset={ls.offset} onOffset={ls.setOffset} />
          </>
        )}
      </Card>
    </div>
  );
}

function PartyEditor({
  papel,
  item,
  onClose,
}: {
  papel: Papel;
  item: Party | null;
  onClose: () => void;
}) {
  const can = useCan();
  const options = useCadastroOptions();
  const f = useForm({
    code: str(item?.code),
    kind: item?.kind ?? "pj",
    name: str(item?.name),
    tradeName: str(item?.tradeName),
    document: item?.document ? formatDocument(item.document) : "",
    stateRegistration: str(item?.stateRegistration),
    ieIndicator: item?.ieIndicator ?? "9",
    municipalRegistration: str(item?.municipalRegistration),
    email: str(item?.email),
    phone: str(item?.phone),
    mobile: str(item?.mobile),
    cep: str(item?.cep),
    street: str(item?.street),
    number: str(item?.number),
    complement: str(item?.complement),
    district: str(item?.district),
    city: str(item?.city),
    cityIbgeCode: str(item?.cityIbgeCode),
    uf: str(item?.uf),
    isCustomer: item ? item.isCustomer : papel === "cliente",
    isSupplier: item ? item.isSupplier : papel === "fornecedor",
    creditLimit: toFormMoney(item?.creditLimit),
    salespersonId: str(item?.salespersonId),
    priceListId: str(item?.priceListId),
    paymentMethodId: str(item?.paymentMethodId),
    notes: str(item?.notes),
    status: item?.status ?? "active",
    // nota fiscal: vazio = decidir pela operação/indicador de IE; não há regra fixa PF/PJ
    finalConsumer:
      item?.finalConsumer === true ? "sim" : item?.finalConsumer === false ? "nao" : "",
    ruralProducer: item?.ruralProducer ?? false,
  });
  const auto = useAutoCode(papel);
  const save = useSave<unknown, Party>({
    resource: "pessoas",
    id: item?.id ?? null,
    invalidate: ["pessoas"],
    onSaved: onClose,
    successMessage: (r, created) =>
      created
        ? `${NOUN[papel]} criado com sucesso. Código: ${r.code}${auto.note(r.code)}.`
        : `${NOUN[papel]} atualizado com sucesso.`,
  });
  const e = f.errors;
  const pf = f.values.kind === "pf";

  function submit(ev: FormEvent) {
    ev.preventDefault();
    const values = {
      ...f.values,
      code: auto.forSave(f.values.code),
      finalConsumer:
        f.values.finalConsumer === "sim" ? true : f.values.finalConsumer === "nao" ? false : null,
    };
    const parsed = item
      ? partyUpdateSchema.safeParse({ ...values, version: item.version })
      : partyInputSchema.safeParse(values);
    if (!parsed.success) return f.setErrors(issuesToErrors(parsed.error.issues));
    f.setErrors({});
    save.mutate(parsed.data);
  }

  return (
    <Card title={item ? `Editar ${item.name}` : TEXT[papel].novo}>
      <form onSubmit={submit} className="space-y-6">
        <fieldset className="grid gap-4 sm:grid-cols-4">
          <legend className="mb-2 text-sm font-semibold">Identificação</legend>
          <CodeField
            auto={auto}
            value={f.values.code}
            onChange={(v) => f.set("code")(v)}
            error={e["code"]}
          />
          <Field label="Tipo">
            <Select {...f.text("kind")}>
              <option value="pj">Pessoa jurídica</option>
              <option value="pf">Pessoa física</option>
            </Select>
          </Field>
          <Field label={pf ? "Nome" : "Razão social"} error={e["name"]} required>
            <Input {...f.text("name")} />
          </Field>
          <Field label={pf ? "Apelido" : "Nome fantasia"} error={e["tradeName"]}>
            <Input {...f.text("tradeName")} />
          </Field>
          <Field label={pf ? "CPF" : "CNPJ"} error={e["document"]}>
            <Input inputMode="numeric" {...f.text("document")} />
          </Field>
          <Field label="Indicador de IE (NF-e)" error={e["ieIndicator"]}>
            <Select {...f.text("ieIndicator")}>
              {Object.entries(IE_INDICATORS).map(([k, v]) => (
                <option key={k} value={k}>
                  {k} — {v}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Inscrição estadual" error={e["stateRegistration"]}>
            <Input {...f.text("stateRegistration")} />
          </Field>
          <Field label="Inscrição municipal" error={e["municipalRegistration"]}>
            <Input {...f.text("municipalRegistration")} />
          </Field>
          <Field
            label="Consumidor final (NF-e)"
            error={e["finalConsumer"]}
            hint="Em branco: a operação fiscal decide"
          >
            <Select {...f.text("finalConsumer")}>
              <option value="">Não definido</option>
              <option value="sim">Sim</option>
              <option value="nao">Não</option>
            </Select>
          </Field>
          <div className="flex items-end pb-2">
            <Checkbox label="Produtor rural" {...f.check("ruralProducer")} />
          </div>
          <div className="flex flex-wrap items-center gap-4 sm:col-span-4">
            <Checkbox
              label="Cliente"
              {...f.check("isCustomer")}
              disabled={!can("cadastros.clientes.gerenciar")}
            />
            <Checkbox
              label="Fornecedor"
              {...f.check("isSupplier")}
              disabled={!can("cadastros.fornecedores.gerenciar")}
            />
            {e["isCustomer"] && <span className="text-xs text-destructive">{e["isCustomer"]}</span>}
          </div>
        </fieldset>

        <fieldset className="grid gap-4 sm:grid-cols-4">
          <legend className="mb-2 text-sm font-semibold">Contato e endereço</legend>
          <Field label="E-mail" error={e["email"]}>
            <Input type="email" {...f.text("email")} />
          </Field>
          <Field label="Telefone" error={e["phone"]}>
            <Input {...f.text("phone")} />
          </Field>
          <Field label="Celular / WhatsApp" error={e["mobile"]}>
            <Input {...f.text("mobile")} />
          </Field>
          <Field label="CEP" error={e["cep"]}>
            <Input inputMode="numeric" {...f.text("cep")} />
          </Field>
          <Field label="Logradouro" error={e["street"]}>
            <Input {...f.text("street")} />
          </Field>
          <Field label="Número" error={e["number"]}>
            <Input {...f.text("number")} />
          </Field>
          <Field label="Complemento" error={e["complement"]}>
            <Input {...f.text("complement")} />
          </Field>
          <Field label="Bairro" error={e["district"]}>
            <Input {...f.text("district")} />
          </Field>
          <Field label="Cidade" error={e["city"]}>
            <Input {...f.text("city")} />
          </Field>
          <Field label="UF" error={e["uf"]}>
            <Select {...f.text("uf")}>
              <option value="">—</option>
              {[...UF_LIST, "EX"].map((uf) => (
                <option key={uf} value={uf}>
                  {uf === "EX" ? "EX (exterior)" : uf}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Código IBGE do município" error={e["cityIbgeCode"]} hint="7 dígitos (NF-e)">
            <Input inputMode="numeric" {...f.text("cityIbgeCode")} />
          </Field>
        </fieldset>

        <fieldset className="grid gap-4 sm:grid-cols-4">
          <legend className="mb-2 text-sm font-semibold">Comercial</legend>
          <Field label="Vendedor" error={e["salespersonId"]}>
            <Select {...f.text("salespersonId")}>
              <option value="">Nenhum</option>
              {options.data?.salespeople.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Tabela de preço" error={e["priceListId"]} hint="Vazio: usa a tabela padrão">
            <Select {...f.text("priceListId")}>
              <option value="">Padrão da empresa</option>
              {options.data?.priceLists.map((l) => (
                <option key={l.id} value={l.id}>
                  {l.name}
                  {l.isDefault ? " (padrão)" : ""}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Forma de pagamento preferida" error={e["paymentMethodId"]}>
            <Select {...f.text("paymentMethodId")}>
              <option value="">Nenhuma</option>
              {options.data?.paymentMethods.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Limite de crédito (R$)" error={e["creditLimit"]} hint="Vazio: sem limite">
            <DecimalInput {...f.text("creditLimit")} />
          </Field>
          <Field label="Situação">
            <Select {...f.text("status")}>
              <option value="active">Ativo</option>
              <option value="inactive">Inativo</option>
            </Select>
          </Field>
          <div className="sm:col-span-3">
            <Field label="Observações" error={e["notes"]}>
              <Input {...f.text("notes")} />
            </Field>
          </div>
        </fieldset>

        <div className="flex gap-2">
          <Button type="submit" disabled={save.isPending}>
            Salvar
          </Button>
          <Button variant="secondary" onClick={onClose}>
            Cancelar
          </Button>
        </div>
        <SaveError error={save.error} />
      </form>
    </Card>
  );
}
